"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
// ✅ CRITICAL: Import directly from source, not from @/contexts barrel
// The barrel re-exports from multiple domains which causes Vite to analyze all dependencies
import { useAuth } from "@/features/identity/contexts/AuthContext";
// ✅ CRITICAL: Import directly, not from @/ui/primitives barrel
// The barrel exports ALL primitives (DataTable, Tabs, Pagination, etc.) which aren't needed for login
import { BaseButton } from "@/ui/primitives/BaseButton";
import { BaseInput } from "@/ui/primitives/BaseInput";
import { PasswordInput } from "../components/PasswordInput";
// ✅ CRITICAL: Import directly from standalone file, not from @/contexts barrel
import { useToastContext } from "@/contexts/toast";
import { validateLogin } from "@/services/shared/utils/validation";
import { toAuthErrorMessage } from "@/services/domains/identity/auth-errors";
import { Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

export function LoginView() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { login } = useAuth();
  const toast = useToastContext();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validation = validateLogin({ email, password });
    if (!validation.isValid) {
      const errorMap: Record<string, string> = {};
      validation.errors.forEach((err) => {
        errorMap[err.field] = err.message;
      });
      setErrors(errorMap);
      return;
    }

    setLoading(true);

    try {
      await login(email, password, rememberMe);

      toast.success("Login successful!");

      // Use router.push for instant client-side navigation
      // User state is already set by login(), no need for full page reload
      navigate("/home");
    } catch (error) {
      const errorMessage = toAuthErrorMessage(error);
      toast.error(errorMessage);
      setErrors({ form: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col lg:flex-row">
      {/* Left Section - Branding & Info (desktop only; hidden on tablet/phone) */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-secondary via-blue-50/30 to-purple-50/20 flex-col justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-12 lg:py-16 order-2 lg:order-1">
        <div className="max-w-2xl mx-auto w-full">
          {/* Logo */}
          <div className="mb-6 sm:mb-8 lg:mb-12">
            <div className="flex items-center gap-3 sm:gap-4 mb-4">
              <img
                src="/images/virial-ai-logo.png"
                alt="Virial AI Logo"
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
              />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary">
                  Virial AI
                </h1>
                <p className="text-xs sm:text-sm text-secondary">
                  Social Media Management
                </p>
              </div>
            </div>

            {/* Main Heading */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4 sm:mb-6 leading-tight">
              Get started with social media management tools from Virial AI —
              Today
            </h2>

            {/* Description */}
            <p className="text-base sm:text-lg text-secondary mb-6 sm:mb-8 leading-relaxed">
              By logging in, you can access all management tools like Content
              Composer, Calendar Planner, Analytics Dashboard, and more to
              help you connect with your audience and grow your social media
              presence.
            </p>

            {/* Benefits Section - hidden on small screens */}
            <div className="mb-0 sm:mb-8 hidden lg:block">
              <h3 className="text-base sm:text-lg font-semibold text-primary mb-3 sm:mb-4">
                Our tools can help you:
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-inverse rounded-full" />
                  </div>
                  <p className="text-secondary text-sm sm:text-base">
                    Spread the word about your business to increase brand
                    awareness
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-inverse rounded-full" />
                  </div>
                  <p className="text-secondary text-sm sm:text-base">
                    Attract new followers, grow your audience and build
                    customer relationships
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-inverse rounded-full" />
                  </div>
                  <p className="text-secondary text-sm sm:text-base">
                    Increase your online engagement by reaching new audiences
                    and tracking performance
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Login Form (shows first on mobile); centered on desktop */}
      <div className="flex-1 flex flex-col justify-center lg:items-center min-h-0 order-1 lg:order-2">
        <div className="w-full max-w-md lg:max-w-none lg:w-[420px] flex-shrink-0 bg-secondary flex flex-col justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-10 lg:py-16 border-t lg:border-t-0 lg:border-l border-primary mx-auto relative">
        {/* Logo fixed top-left on tablets/phones only */}
        <div className="fixed top-4 left-4 z-10 flex items-center gap-3 lg:hidden">
          <img
            src="/images/virial-ai-logo.png"
            alt="Virial AI"
            className="w-10 h-10 sm:w-12 sm:h-12 object-contain shrink-0"
          />
          <span className="text-lg sm:text-xl font-bold text-primary">
            Virial AI
          </span>
        </div>
        <div className="w-full min-w-0">
          {/* Social Login Buttons */}
          <div className="space-y-3 mb-4 sm:mb-6">
            <button
              type="button"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 min-h-[44px] border border-primary rounded-lg bg-secondary hover:bg-hover transition-colors text-primary font-medium text-sm sm:text-[15px]"
            >
              <Facebook className="w-5 h-5 shrink-0 text-accent-primary" />
              Log in with Facebook
            </button>
            <button
              type="button"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 min-h-[44px] border border-primary rounded-lg bg-secondary hover:bg-hover transition-colors text-primary font-medium text-sm sm:text-[15px]"
            >
              <Instagram className="w-5 h-5 shrink-0 text-[#E4405F]" />
              Log in with Instagram
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-4 sm:mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-primary"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-secondary text-secondary">or</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-4 sm:mb-6">
            {errors.form && (
              <div className="p-3 bg-status-error-bg border border-status-error rounded-lg">
                <p className="text-sm text-status-error">{errors.form}</p>
              </div>
            )}

            <div>
              <BaseInput
                type="email"
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: "" }));
                  }
                }}
                placeholder="Email address"
                required
                disabled={loading}
                error={errors.email}
                size="lg"
                autoComplete="username"
              />
            </div>

            <div>
              <PasswordInput
                value={password}
                onChange={(value) => {
                  setPassword(value);
                  if (errors.password) {
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }
                }}
                placeholder="Password"
                required
                disabled={loading}
                error={errors.password}
                size="lg"
                autoComplete="current-password"
              />
            </div>

            {/* Stay signed in checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 text-accent-primary bg-secondary border-primary rounded focus:ring-accent-primary focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label
                htmlFor="rememberMe"
                className="ml-2 text-sm text-secondary cursor-pointer select-none disabled:opacity-50"
              >
                Stay signed in
              </label>
            </div>

            <BaseButton
              type="submit"
              variant="primary"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Log in"}
            </BaseButton>
          </form>

          {/* Create Account Link */}
          <div className="text-center mb-6 sm:mb-8">
            <Link
              to="/signup"
              className="text-accent-primary hover:underline text-sm sm:text-[15px] font-medium"
            >
              Create new account
            </Link>
          </div>

        </div>
        </div>
      </div>
    </div>
  );
}
